"use client";

/**
 * usePageTitle — sets document.title from a client component.
 *
 * Most of our pages are "use client" and so can't export Next.js `metadata`
 * directly (that's a server-only API). This hook is the pragmatic
 * equivalent: it updates document.title on mount and on every change to the
 * passed title.
 *
 * The hook restores the previous title when the component unmounts, so a
 * nested transient surface (e.g., a modal page) doesn't permanently
 * overwrite the parent route's title.
 *
 * Pair with <RouteAnnouncer/> in providers.tsx — the announcer reads
 * document.title after each route change and speaks it via aria-live.
 */

import { useEffect } from "react";

export function usePageTitle(title: string) {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      // Don't overwrite if a sibling effect already set a newer title.
      if (document.title === title) document.title = previous;
    };
  }, [title]);
}
